/* global window,document */
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { JssProvider, SheetsRegistry } from 'react-jss';
import ready from 'domready';
import lazysizes from 'lazysizes';
import createStore from './store';
import { REHYDRATED } from './actions';
import App from './components/App';
import { Client as GlobalMarkup } from './components/GlobalMarkup';
import createJss from './styles/createJss';
import isIE11 from './helpers/isIE11';
import createPublicApi from './publicApi';

function maybeLoadPrismicToolbar() {
  if (!window.prismic && !isIE11) {
    const script = document.createElement('script');
    script.async = 1;
    script.defer = 1;
    script.src = process.env.REACT_APP_PRISMIC_TOOLBAR_URL;
    document.head.appendChild(script);
  }
}

const isElementTagName = (el, ...tagNames) => el && tagNames.indexOf(el.tagName) >= 0;

ready(() => {
  const mountPoints = document.getElementsByClassName('ti-react-component');
  const hotCallbacks = [];
  Array.prototype.forEach.call(mountPoints, parent => {
    // Remove any iframes injected by third-party scripts (e.g. Zendesk) before
    // reading children by index.
    const children = Array.prototype.filter.call(parent.children, el => el.tagName !== 'IFRAME');

    // can assume the children are always in this order:
    const cssEl = children[0]; // (not doing [a, b, c] = ... because IE11 needs a polyfill)
    const preloadedStateEl = children[1];
    const globalMarkupRoot = children[2];
    const root = children[3];

    if (isElementTagName(cssEl, 'LINK', 'STYLE')
      && isElementTagName(preloadedStateEl, 'SCRIPT')
      && isElementTagName(globalMarkupRoot, 'DIV')
      && isElementTagName(root, 'DIV')) {
      const preloadedState = JSON.parse(preloadedStateEl.textContent);

      // stop it cluttering up the DOM
      parent.removeChild(preloadedStateEl);

      const sheets = new SheetsRegistry();
      const store = createStore(preloadedState);
      const jss = createJss();

      // Creates the public api used to reload new content.
      // Will be used by the playbook to load content
      window.contentService = createPublicApi(store);

      // JSS is careful to output a minimal set of stylesheets - so if we reuse a component in more
      // than one rendering tree, its stylesheet will only be output once.
      //
      // However our server maintains no state between web service requests, so it cannot recognise
      // when a component has been reused in more than one rendering tree. We need to make sure the
      // client generates the same classNames as the server does (otherwise hydration will fail), so
      // we need to reset the SheetsManager whenever we render a component that corresponds to a
      // different web service endpoint.
      //
      // On the server, react-jss resets the SheetsManager in createHoc() whenever we pass a new
      // SheetsRegistry to the JssProvider; so we need to pass a SheetsRegistry instance here to get
      // the same behaviour.
      //
      // (Side note: I would rather not use SheetsRegistry on the client, but it seems like the best
      // way. I also tried passing a different `theme` object to the ThemeProvider per tree, but
      // that only works if all components use injectSheet with a function - react-jss doesn't
      // enable theming for components that call injectSheet with a simple rules object. That would
      // be easy to forget when writing components, so I think it's better to use SheetsRegistry
      // here - it has hardly any overhead anyway.)
      //
      const renderWithProviders = RootComponent => (
        <Provider store={store}>
          <JssProvider registry={sheets} jss={jss}>
            <RootComponent />
          </JssProvider>
        </Provider>
      );
      ReactDOM.hydrate(
        renderWithProviders(App),
        root,
        () => {
          // We don't need the static css any more once we have launched our application.
          parent.removeChild(cssEl);
          store.dispatch({ type: REHYDRATED });
          maybeLoadPrismicToolbar();
        },
      );
      ReactDOM.hydrate(<GlobalMarkup />, globalMarkupRoot);

      /* istanbul ignore next: exclude HMR code path from test coverage calculations */
      if (module.hot) {
        hotCallbacks.push(NextApp => {
          ReactDOM.render(renderWithProviders(NextApp), root);
        });
      }
    }
  });

  /* istanbul ignore next */
  if (module.hot) {
    module.hot.accept('./components/App', () => {
      // eslint-disable-next-line global-require
      const NextApp = require('./components/App').default;
      hotCallbacks.forEach(callback => {
        callback(NextApp);
      });
    });
  }

  lazysizes.init();
});
